import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { resolveReadableHttpErrorMessage } from "../../../lib/http/errors";
import { financePayableSummaryQueryKey, financePaymentRequestQueryKey } from "../config";
import {
  downloadFinancePaymentRequestAttachment,
  fetchFinancePaymentRequestDetail,
  withdrawFinancePaymentRequest,
} from "../repo/financePaymentRequestRepo";
import type { FinancePaymentRequestAttachment } from "../types";
import { saveFinanceFile } from "./saveFinanceFile";

/** 请款详情：读一张请款单；附件带登录凭据取回再交给浏览器保存。 */
export function useFinancePaymentRequestDetailRuntime(requestId: string) {
  const detail = useQuery({
    queryKey: [...financePaymentRequestQueryKey, "detail", requestId],
    queryFn: () => fetchFinancePaymentRequestDetail(requestId),
    // 看不到或单子不存在时重试没有意义；网络抖动由 httpClient 自己重试。
    retry: false,
    refetchOnWindowFocus: false,
  });
  const [downloadError, setDownloadError] = useState("");
  return {
    detail: detail.data ?? null,
    loading: detail.isPending,
    error: detail.error ? resolveReadableHttpErrorMessage(detail.error, "请款单读取失败，请重试。") : "",
    refresh() {
      void detail.refetch();
    },
    downloadError,
    /** 取回附件后用临时链接触发保存，文件名用上传时的原名。 */
    async download(attachment: FinancePaymentRequestAttachment) {
      setDownloadError("");
      try {
        const blob = await downloadFinancePaymentRequestAttachment(requestId, attachment.attachmentId);
        saveFinanceFile(blob, attachment.fileName);
      } catch (error) {
        setDownloadError(resolveReadableHttpErrorMessage(error, "附件下载失败，请重试。"));
      }
    },
  };
}

/**
 * 撤回请款：确认后提交，成功后定向刷新请款详情、请款记录和应付汇总（撤回的不再算已请款，可请额度回来）；
 * 后端（审批模块）拦下时原样显示原因。防重复点击。
 */
export function useFinancePaymentRequestWithdraw(requestId: string) {
  const client = useQueryClient();
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submitLock = useRef(false);

  return {
    error,
    submitting,
    /** 撤回，成功返回真。 */
    async withdraw(): Promise<boolean> {
      if (submitLock.current) return false;
      submitLock.current = true;
      setSubmitting(true);
      setError("");
      try {
        await withdrawFinancePaymentRequest(requestId);
        void client.invalidateQueries({ queryKey: financePaymentRequestQueryKey });
        void client.invalidateQueries({ queryKey: financePayableSummaryQueryKey });
        return true;
      } catch (withdrawFailure) {
        setError(resolveReadableHttpErrorMessage(withdrawFailure, "撤回失败，请稍后重试。"));
        return false;
      } finally {
        submitLock.current = false;
        setSubmitting(false);
      }
    },
  };
}
